/**
 * Pre-interview routing screen (PI-SPEC-SW-978).
 *
 * The reporter chooses where the case is filed *before* the first interview
 * question, using the FogBugz "New Case" header they already know: same fields,
 * same labels, same order. The interview then replaces only the free-text
 * description box.
 *
 * Two rules come straight from the E195 end-to-end findings log and are the
 * reason this screen exists:
 *
 * 1. **No silent defaults.** Project has no pre-selected value and gates the
 *    start control. Case #17054 was filed into the API key's default project,
 *    in that project's default area, assigned to a departed employee, because
 *    the old picker sat below the intent editor pre-set to "Use my default
 *    project" and required no interaction to pass (findings log items 1, 3, 6,
 *    10).
 * 2. **Every default is visible and editable.** Area, Milestone, Category,
 *    Assigned To, and Priority may pre-fill from the FogBugz lists — as FogBugz
 *    itself does — but always in a control the reporter can see and change.
 *
 * Dependent filtering is pure client-side derivation: `cmd=listAreas` and
 * `cmd=listFixFors` take no project parameter, so the server returns every row
 * once and narrowing is done here rather than with a request per selection.
 */

import { useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type {
  FogbugzAreaOption,
  FogbugzMilestoneOption,
  FogbugzPersonOption,
  FogbugzProjectOption,
  RoutingOptions,
  RoutingSelection,
} from "@/hooks/queries/use-fogbugz";

/** At most 10 tags of 50 characters — mirrors `routingSelectionSchema`. */
const MAX_TAGS = 10;
const MAX_TAG_LENGTH = 50;
/** FogBugz's own default milestone, and a global one on every instance. */
const DEFAULT_MILESTONE_NAME = "Undecided";
/** FogBugz's own default category for a new case. */
const DEFAULT_CATEGORY_NAME = "Bug";

export interface RoutingScreenProps {
  /** Every FogBugz list to choose from, fetched in one request. */
  options: RoutingOptions;
  /** Called with the reporter's selection when they start the interview. */
  onStart: (routing: RoutingSelection) => void;
  /** True while the start request is in flight. */
  isStarting?: boolean | undefined;
  /**
   * Failure from a previous start attempt, rendered inline so the reporter's
   * selections survive a retry instead of being thrown away with the screen.
   */
  startError?: string | null | undefined;
  /**
   * Priority to pre-select, when the caller already has a severity-derived one.
   * Falls back to the FogBugz default priority. Always overridable.
   */
  initialPriorityIx?: number | undefined;
  /**
   * The submit button's words. Defaults to "Start interview"; the Toolbox
   * FogBugz Formulator asks what is being filed next, so it says so.
   */
  submitLabel?: string | undefined;
  /**
   * Start with every field unchosen: no default Category or Priority either
   * (feature PI-SPEC-SW-1631). The Toolbox FogBugz Formulator sets it when it
   * was opened from a Product Q&A answer, so nothing on the screen reads as
   * chosen by the hand-off; the person picks every field. Choosing a project
   * still shows that project's FogBugz defaults, as the person's own step.
   */
  startUnchosen?: boolean | undefined;
  className?: string | undefined;
}

/** A milestone applies to a project when it is that project's or global. */
function milestoneAppliesTo(milestone: FogbugzMilestoneOption, ixProject: number): boolean {
  // Global milestones ("Undecided" among them) arrive with ixProject absent or
  // 0 and must stay selectable for every project.
  return (
    milestone.ixProject === undefined || milestone.ixProject === 0 || milestone.ixProject === ixProject
  );
}

/** Display name for a FogBugz person, preferring their full name. */
function personName(person: FogbugzPersonOption): string {
  return person.sFullName ?? person.sPerson ?? person.sEmail;
}

/** Read a positive integer from a passthrough FogBugz row, if present. */
function readIx(row: Record<string, unknown>, key: string): number | undefined {
  const value = row[key];
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : undefined;
}

/** True when a passthrough FogBugz row is flagged as its list's default. */
function isFlaggedDefault(row: Record<string, unknown>): boolean {
  return row.fDefault === true;
}

/**
 * The area FogBugz's own New Case screen would pre-select: the one flagged as
 * default, else the project's first area in FogBugz's ordering.
 */
function defaultAreaIx(areas: FogbugzAreaOption[]): number | null {
  const flagged = areas.find(isFlaggedDefault);
  return flagged?.ixArea ?? areas[0]?.ixArea ?? null;
}

/** "Undecided" when the project can see it, else the first milestone. */
function defaultMilestoneIx(milestones: FogbugzMilestoneOption[]): number | null {
  const undecided = milestones.find(
    (milestone) => milestone.sFixFor.trim().toLowerCase() === DEFAULT_MILESTONE_NAME.toLowerCase()
  );
  return undecided?.ixFixFor ?? milestones[0]?.ixFixFor ?? null;
}

/**
 * The project's primary contact, which is who FogBugz would assign the case to
 * anyway — shown here rather than applied silently. Departed employees are
 * already filtered out of `people` server-side, so an unresolvable contact
 * leaves the field for the reporter to fill instead of naming someone who has
 * left (findings log item 10).
 */
function defaultAssigneeIx(
  project: FogbugzProjectOption,
  people: FogbugzPersonOption[]
): number | null {
  const candidate =
    readIx(project, "ixPersonPrimaryContact") ??
    readIx(project, "ixPersonOwner") ??
    readIx(project, "ixPersonAssignedTo");
  if (candidate === undefined) return null;
  return people.some((person) => person.ixPerson === candidate) ? candidate : null;
}

/** Parse the comma-separated tag field into trimmed, de-duplicated entries. */
function parseTags(raw: string): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const part of raw.split(",")) {
    const tag = part.trim();
    if (tag === "" || seen.has(tag)) continue;
    seen.add(tag);
    tags.push(tag);
  }
  return tags;
}

interface RoutingSelectProps {
  id: string;
  /** Machine-readable field name; also drives field-order assertions. */
  field: string;
  label: string;
  value: number | null;
  entries: { value: number; label: string }[];
  /** Non-selectable prompt shown while nothing is chosen. */
  placeholder: string;
  onChange: (value: number | null) => void;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  hint?: string | undefined;
}

/**
 * One labelled FogBugz dropdown.
 *
 * A native `<select>` on purpose: it is what the FogBugz New Case screen uses,
 * it matches the existing FogBugz project picker in `intent-review.tsx`, and its
 * placeholder can be a genuinely non-selectable `<option disabled>` rather than
 * an entry the reporter could pick to mean "decide for me".
 */
function RoutingSelect({
  id,
  field,
  label,
  value,
  entries,
  placeholder,
  onChange,
  required = false,
  disabled = false,
  hint,
}: RoutingSelectProps) {
  const hintId = `${id}-hint`;

  return (
    <div className="space-y-1.5" data-routing-field={field}>
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span className="text-red-400" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </Label>
      <select
        id={id}
        name={field}
        value={value === null ? "" : String(value)}
        required={required}
        disabled={disabled}
        aria-describedby={hint ? hintId : undefined}
        onChange={(event) => {
          onChange(event.target.value === "" ? null : Number(event.target.value));
        }}
        className={cn(
          "flex h-11 md:h-9 w-full items-center rounded-xl border border-white/20 bg-white/[0.06]",
          "px-4 text-[16px] md:text-xs font-medium text-foreground",
          "focus:outline-none focus:ring-1 focus:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "hover:bg-white/[0.10] hover:border-white/30 transition-all duration-200"
        )}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {entries.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {entry.label}
          </option>
        ))}
      </select>
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The FogBugz New Case header, in front of the interview.
 *
 * Fields render top to bottom in FogBugz's own order: Project, Area, Milestone,
 * Category, Assigned To, Priority, Tags, Version.
 */
export function RoutingScreen({
  options,
  onStart,
  isStarting = false,
  startError,
  initialPriorityIx,
  submitLabel = "Start interview",
  startUnchosen = false,
  className,
}: RoutingScreenProps) {
  const [ixProject, setIxProject] = useState<number | null>(null);
  const [ixArea, setIxArea] = useState<number | null>(null);
  const [ixFixFor, setIxFixFor] = useState<number | null>(null);
  const [ixCategory, setIxCategory] = useState<number | null>(() => {
    if (startUnchosen) return null;
    return (
      options.categories.find(
        (category) => category.sCategory.trim().toLowerCase() === DEFAULT_CATEGORY_NAME.toLowerCase()
      )?.ixCategory ??
      options.categories.find(isFlaggedDefault)?.ixCategory ??
      options.categories[0]?.ixCategory ??
      null
    );
  });
  const [ixPersonAssignedTo, setIxPersonAssignedTo] = useState<number | null>(null);
  const [ixPriority, setIxPriority] = useState<number | null>(() => {
    if (startUnchosen) return null;
    const severityDerived =
      initialPriorityIx !== undefined &&
      options.priorities.some((priority) => priority.ixPriority === initialPriorityIx)
        ? initialPriorityIx
        : undefined;
    return (
      severityDerived ??
      options.priorities.find(isFlaggedDefault)?.ixPriority ??
      options.priorities[0]?.ixPriority ??
      null
    );
  });
  const [tagsText, setTagsText] = useState("");
  const [version, setVersion] = useState("");

  const project = useMemo(
    () => options.projects.find((candidate) => candidate.ixProject === ixProject) ?? null,
    [options.projects, ixProject]
  );

  // Dependent lists, derived from the single payload rather than refetched.
  const areasForProject = useMemo(
    () =>
      ixProject === null ? [] : options.areas.filter((area) => area.ixProject === ixProject),
    [options.areas, ixProject]
  );
  const milestonesForProject = useMemo(
    () =>
      ixProject === null
        ? []
        : options.milestones.filter((milestone) => milestoneAppliesTo(milestone, ixProject)),
    [options.milestones, ixProject]
  );

  const tags = useMemo(() => parseTags(tagsText), [tagsText]);
  const tagsError =
    tags.length > MAX_TAGS
      ? `At most ${String(MAX_TAGS)} tags.`
      : tags.some((tag) => tag.length > MAX_TAG_LENGTH)
        ? `Each tag can be at most ${String(MAX_TAG_LENGTH)} characters.`
        : null;

  /**
   * Re-route the whole form onto the chosen project: narrow the dependent lists,
   * drop any selection that is no longer in them, and pre-fill the new project's
   * FogBugz defaults into the visible controls.
   */
  function handleProjectChange(next: number | null) {
    setIxProject(next);

    if (next === null) {
      setIxArea(null);
      setIxFixFor(null);
      setIxPersonAssignedTo(null);
      return;
    }

    const nextAreas = options.areas.filter((area) => area.ixProject === next);
    const nextMilestones = options.milestones.filter((milestone) =>
      milestoneAppliesTo(milestone, next)
    );

    setIxArea((current) =>
      current !== null && nextAreas.some((area) => area.ixArea === current)
        ? current
        : defaultAreaIx(nextAreas)
    );
    setIxFixFor((current) =>
      current !== null && nextMilestones.some((milestone) => milestone.ixFixFor === current)
        ? current
        : defaultMilestoneIx(nextMilestones)
    );

    const nextProject = options.projects.find((candidate) => candidate.ixProject === next);
    setIxPersonAssignedTo(
      nextProject ? defaultAssigneeIx(nextProject, options.people) : null
    );
  }

  const canStart = project !== null && tagsError === null && !isStarting;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (project === null || tagsError !== null || isStarting) return;

    const routing: RoutingSelection = {
      ixProject: project.ixProject,
      sProject: project.sProject,
    };

    const area = areasForProject.find((candidate) => candidate.ixArea === ixArea);
    if (area) {
      routing.ixArea = area.ixArea;
      routing.sArea = area.sArea;
    }

    const milestone = milestonesForProject.find((candidate) => candidate.ixFixFor === ixFixFor);
    if (milestone) {
      routing.ixFixFor = milestone.ixFixFor;
      routing.sFixFor = milestone.sFixFor;
    }

    const category = options.categories.find((candidate) => candidate.ixCategory === ixCategory);
    if (category) {
      routing.ixCategory = category.ixCategory;
      routing.sCategory = category.sCategory;
    }

    const assignee = options.people.find((candidate) => candidate.ixPerson === ixPersonAssignedTo);
    if (assignee) {
      routing.ixPersonAssignedTo = assignee.ixPerson;
      routing.sPersonAssignedTo = personName(assignee);
    }

    const priority = options.priorities.find((candidate) => candidate.ixPriority === ixPriority);
    if (priority) {
      routing.ixPriority = priority.ixPriority;
    }

    if (tags.length > 0) {
      routing.tags = tags;
    }

    const trimmedVersion = version.trim();
    if (trimmedVersion !== "") {
      routing.version = trimmedVersion;
    }

    onStart(routing);
  }

  const hasProject = project !== null;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="routing-screen"
      className={cn("glass-card rounded-2xl border border-white/[0.08] p-6 space-y-5", className)}
    >
      <div className="space-y-1">
        <h2 className="text-base font-semibold">Where should this case be filed?</h2>
        <p className="text-sm text-muted-foreground">
          These are the FogBugz fields your case will carry, in the order the New Case screen uses.
          Pick the project first — the interview replaces only the description.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <RoutingSelect
          id="routing-project"
          field="project"
          label="Project"
          required
          value={ixProject}
          placeholder="Select a project"
          entries={options.projects.map((candidate) => ({
            value: candidate.ixProject,
            label: candidate.sProject,
          }))}
          onChange={handleProjectChange}
          hint="Required. Nothing is filed until you choose."
        />

        <RoutingSelect
          id="routing-area"
          field="area"
          label="Area"
          value={ixArea}
          placeholder={hasProject ? "Select an area" : "Choose a project first"}
          entries={areasForProject.map((area) => ({ value: area.ixArea, label: area.sArea }))}
          onChange={setIxArea}
          disabled={!hasProject || areasForProject.length === 0}
          hint={
            hasProject && areasForProject.length === 0
              ? "This project has no areas in FogBugz."
              : undefined
          }
        />

        <RoutingSelect
          id="routing-milestone"
          field="milestone"
          label="Milestone"
          value={ixFixFor}
          placeholder={hasProject ? "Select a milestone" : "Choose a project first"}
          entries={milestonesForProject.map((milestone) => ({
            value: milestone.ixFixFor,
            label: milestone.sFixFor,
          }))}
          onChange={setIxFixFor}
          disabled={!hasProject || milestonesForProject.length === 0}
          hint={
            hasProject && milestonesForProject.length === 0
              ? "This project has no milestones in FogBugz."
              : undefined
          }
        />

        <RoutingSelect
          id="routing-category"
          field="category"
          label="Category"
          value={ixCategory}
          placeholder="Select a category"
          entries={options.categories.map((category) => ({
            value: category.ixCategory,
            label: category.sCategory,
          }))}
          onChange={setIxCategory}
          disabled={options.categories.length === 0}
          hint={
            options.categories.length === 0
              ? "Categories are unavailable from FogBugz, so FogBugz will apply its own category."
              : undefined
          }
        />

        <RoutingSelect
          id="routing-assigned-to"
          field="assignedTo"
          label="Assigned To"
          value={ixPersonAssignedTo}
          placeholder="Select a person"
          entries={options.people.map((person) => ({
            value: person.ixPerson,
            label: personName(person),
          }))}
          onChange={setIxPersonAssignedTo}
          disabled={options.people.length === 0}
          hint={
            hasProject && ixPersonAssignedTo !== null
              ? "Pre-filled from this project in FogBugz. Change it if it should go to someone else."
              : undefined
          }
        />

        <RoutingSelect
          id="routing-priority"
          field="priority"
          label="Priority"
          value={ixPriority}
          placeholder="Select a priority"
          entries={options.priorities.map((priority) => ({
            value: priority.ixPriority,
            label: priority.sPriority,
          }))}
          onChange={setIxPriority}
          disabled={options.priorities.length === 0}
        />

        <div className="space-y-1.5" data-routing-field="tags">
          <Label htmlFor="routing-tags">
            Tags
            <span className="font-normal text-muted-foreground">{" (optional)"}</span>
          </Label>
          <Input
            id="routing-tags"
            name="tags"
            value={tagsText}
            placeholder="security, mobile"
            aria-describedby="routing-tags-hint"
            aria-invalid={tagsError !== null}
            onChange={(event) => {
              setTagsText(event.target.value);
            }}
          />
          <p
            id="routing-tags-hint"
            className={cn("text-xs", tagsError ? "text-red-400" : "text-muted-foreground")}
          >
            {tagsError ??
              `Comma-separated, up to ${String(MAX_TAGS)} tags. ${String(tags.length)} entered.`}
          </p>
        </div>

        <div className="space-y-1.5" data-routing-field="version">
          <Label htmlFor="routing-version">
            Version
            <span className="font-normal text-muted-foreground">{" (optional)"}</span>
          </Label>
          <Input
            id="routing-version"
            name="version"
            value={version}
            placeholder="2.3.0"
            onChange={(event) => {
              setVersion(event.target.value);
            }}
          />
        </div>
      </div>

      {startError ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{startError}</span>
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!canStart}>
          {isStarting ? "Starting interview..." : submitLabel}
        </Button>
        {!hasProject ? (
          <p className="text-xs text-muted-foreground">Choose a project to begin.</p>
        ) : null}
      </div>
    </form>
  );
}
